/**
 * Pre-matching flow for the Import Job Description IQ overlay (Rules 3a, 13–17).
 *
 * This module is intentionally pure: it owns the session state machine and the
 * exact chat copy/actions, but it never touches the network, Vue, or Pinia. The
 * IQOverlay component drives it by calling the transition methods below and
 * posting the returned chat turns via the existing IQAIResponse component.
 *
 * Session stage order (concept page / Rules):
 *   upload → title-mismatch check (Rule 3a) → extraction read-out (Rule 13)
 *   → best-practices opt-in (Rule 14) → improvements review (Rule 15)
 *   → missing-responsibilities review (Rule 16) → readiness gate (Rule 17)
 *
 * Catalog matching (Rule 5) is out of scope here; the session only hands off the
 * final working bullet list to it.
 */

export const POSITION_IMPORT_PHASE = Object.freeze({
  UPLOAD: "upload",
  TITLE_MISMATCH: "titleMismatch",
  READOUT: "readout",
  OPT_IN: "optIn",
  IMPROVEMENTS: "improvements",
  MISSING: "missing",
  GATE: "gate",
  DONE: "done",
  CANCELLED: "cancelled",
  EMPTY: "empty",
});

export const POSITION_IMPORT_ACTION = Object.freeze({
  CHOOSE_FILE: "position-import-choose-file",
  CANCEL_IMPORT: "position-import-cancel",
  UPLOAD_DIFFERENT_FILE: "position-import-upload-different-file",
  ABORT: "position-import-abort",
  CONTINUE_AS_IS: "position-import-continue",
  BENCHMARK_YES: "position-import-benchmark-yes",
  BENCHMARK_NO: "position-import-benchmark-no",
  IMPROVE: "position-import-improve",
  LOVE: "position-import-love",
  SKIP: "position-import-skip",
  PROCEED: "position-import-proceed",
  NOT_YET: "position-import-not-yet",
});

const toAction = (label, type) => ({ label, type });

/**
 * Rule 4 / initial state: prompt for the job-description document.
 */
export function buildUploadPromptMessage(positionName) {
  const target = positionName ? ` for ${positionName}` : "";
  return {
    answer: `Please upload the job description document (.docx or .pdf) so I can extract its responsibilities${target}.`,
    actions: [toAction("Choose File", POSITION_IMPORT_ACTION.CHOOSE_FILE)],
  };
}

/**
 * Rule 3a — verbatim copy from the rules page.
 */
export function buildTitleMismatchMessage({ positionName, jdTitle }) {
  return {
    answer: `The file you imported contains job description information but for a job with a different title. You are importing a job description for a ${positionName} but the one you imported is for a ${jdTitle}. Do you want to still process job responsibilities from this file?`,
    actions: [
      toAction("Cancel Import", POSITION_IMPORT_ACTION.CANCEL_IMPORT),
      toAction("Continue as is", POSITION_IMPORT_ACTION.CONTINUE_AS_IS),
    ],
  };
}

/**
 * Rule 13 — plain ordered read-out of every extracted bullet.
 */
export function buildExtractionReadoutMessage(bullets) {
  const count = Array.isArray(bullets) ? bullets.length : 0;
  const noun = count === 1 ? "responsibility" : "responsibilities";
  const lines = [`I extracted ${count} ${noun} from the document:`];
  (bullets || []).forEach((bullet) => lines.push(`* ${bullet}`));
  return { answer: lines.join("\n"), actions: [] };
}

/**
 * Rule 4a — zero bullets means the document is not a job description.
 * Offer "Upload a different file" (re-opens the picker within the same session
 * and re-triggers extraction) and "Cancel" (aborts the session and closes the
 * overlay). Both actions are handled by the IQOverlay component.
 */
export function buildNotJobDescriptionMessage() {
  return {
    answer:
      "I could not find any responsibility of a job description in the file. Upload a file that contains a job description.",
    actions: [
      toAction("Upload a different file", POSITION_IMPORT_ACTION.UPLOAD_DIFFERENT_FILE),
      toAction("Cancel", POSITION_IMPORT_ACTION.ABORT),
    ],
  };
}

/**
 * Rule 14 — best-practices comparison opt-in.
 */
export function buildBenchmarkOptInMessage(positionName) {
  return {
    answer: `Would you also like me to compare this job description against best practices for ${positionName}?`,
    actions: [
      toAction("Yes", POSITION_IMPORT_ACTION.BENCHMARK_YES),
      toAction("No", POSITION_IMPORT_ACTION.BENCHMARK_NO),
    ],
  };
}

/**
 * Rule 15 — side-by-side original vs. IQ's rewrite.
 */
export function buildImprovementReviewMessage({ original, rewrite }) {
  return {
    answer: `Here's a best-practice improvement for one of the extracted responsibilities:\n\n**Original:**\n${original}\n\n**Improved:**\n${rewrite}`,
    actions: [
      toAction("Improve", POSITION_IMPORT_ACTION.IMPROVE),
      toAction("Love", POSITION_IMPORT_ACTION.LOVE),
      toAction("Skip", POSITION_IMPORT_ACTION.SKIP),
    ],
  };
}

/**
 * Rule 16 — a title-benchmark responsibility the document did not contain.
 */
export function buildMissingReviewMessage({ text }) {
  return {
    answer: `Your position's title benchmark suggests this responsibility that isn't in the document:\n\n${text}`,
    actions: [
      toAction("Improve", POSITION_IMPORT_ACTION.IMPROVE),
      toAction("Love", POSITION_IMPORT_ACTION.LOVE),
      toAction("Skip", POSITION_IMPORT_ACTION.SKIP),
    ],
  };
}

/**
 * Rule 17 — readiness gate. Matching does not begin until the user confirms.
 */
export function buildReadinessGateMessage() {
  return {
    answer: "Ready to proceed with import and matching?",
    actions: [
      toAction("Yes, proceed", POSITION_IMPORT_ACTION.PROCEED),
      toAction("Not yet", POSITION_IMPORT_ACTION.NOT_YET),
    ],
  };
}

/**
 * Create an import session. `positionName` is the Position title used in Rule 3a,
 * Rule 14 and the benchmark payloads.
 */
export function createPositionImportSession({ positionId = null, positionName = "" } = {}) {
  const state = {
    positionId,
    positionName,
    phase: POSITION_IMPORT_PHASE.UPLOAD,
    bullets: [],
    jdTitle: null,
    titleMismatch: false,
    benchmarkOptIn: false,
    improvementQueue: [],
    improvementCursor: 0,
    missingQueue: [],
    missingCursor: 0,
  };

  const initialMessage = () => buildUploadPromptMessage(state.positionName);

  const readoutAndOptIn = () => [
    buildExtractionReadoutMessage(state.bullets),
    buildBenchmarkOptInMessage(state.positionName),
  ];

  const applyExtraction = ({ bullets = [], titleMismatch = false, jdTitle = null } = {}) => {
    state.bullets = Array.isArray(bullets) ? bullets : [];
    state.titleMismatch = !!titleMismatch;
    state.jdTitle = jdTitle || null;

    if (state.bullets.length === 0) {
      state.phase = POSITION_IMPORT_PHASE.EMPTY;
      return [buildNotJobDescriptionMessage()];
    }

    if (state.titleMismatch && state.jdTitle) {
      state.phase = POSITION_IMPORT_PHASE.TITLE_MISMATCH;
      return [
        buildTitleMismatchMessage({
          positionName: state.positionName,
          jdTitle: state.jdTitle,
        }),
      ];
    }

    state.phase = POSITION_IMPORT_PHASE.OPT_IN;
    return readoutAndOptIn();
  };

  const resolveTitleMismatch = (choice) => {
    if (choice === POSITION_IMPORT_ACTION.CANCEL_IMPORT) {
      state.phase = POSITION_IMPORT_PHASE.CANCELLED;
      return [];
    }
    state.phase = POSITION_IMPORT_PHASE.OPT_IN;
    return readoutAndOptIn();
  };

  const resolveOptIn = (choice) => {
    if (choice === POSITION_IMPORT_ACTION.BENCHMARK_NO) {
      state.benchmarkOptIn = false;
      state.phase = POSITION_IMPORT_PHASE.GATE;
      return [buildReadinessGateMessage()];
    }
    state.benchmarkOptIn = true;
    state.phase = POSITION_IMPORT_PHASE.IMPROVEMENTS;
    return [];
  };

  /**
   * Populate the Rule 15 queue from the benchmark-improvement API response.
   * Only bullets with a proposed rewrite need a review step.
   */
  const loadImprovements = (improvements = []) => {
    state.improvementQueue = improvements
      .map((item, index) => ({ ...item, index }))
      .filter((item) => item.rewrite && item.rewrite !== item.original);
    state.improvementCursor = 0;
    return state.improvementQueue.length > 0
      ? [buildImprovementReviewMessage(currentImprovement())]
      : [];
  };

  const currentImprovement = () => state.improvementQueue[state.improvementCursor] || null;

  const resolveImprovement = (choice, editedText = null) => {
    const current = currentImprovement();
    if (!current) return [];

    if (choice === POSITION_IMPORT_ACTION.LOVE) {
      const next = editedText != null && editedText !== "" ? editedText : current.rewrite;
      state.bullets[current.index] = next;
    }

    state.improvementCursor += 1;
    const nextImprovement = currentImprovement();
    if (nextImprovement) {
      return [buildImprovementReviewMessage(nextImprovement)];
    }

    // Improvements exhausted → move on (caller transitions to Rule 16).
    return [];
  };

  const loadMissing = (missing = []) => {
    state.missingQueue = Array.isArray(missing)
      ? missing.map((text) => ({ text }))
      : [];
    state.missingCursor = 0;
    state.phase = POSITION_IMPORT_PHASE.MISSING;
    return state.missingQueue.length > 0 ? [buildMissingReviewMessage(currentMissing())] : [];
  };

  const currentMissing = () => state.missingQueue[state.missingCursor] || null;

  const resolveMissing = (choice, editedText = null) => {
    const current = currentMissing();
    if (!current) return [];

    if (choice === POSITION_IMPORT_ACTION.LOVE) {
      const next = editedText != null && editedText !== "" ? editedText : current.text;
      state.bullets.push(next);
    }

    state.missingCursor += 1;
    const nextMissing = currentMissing();
    if (nextMissing) {
      return [buildMissingReviewMessage(nextMissing)];
    }

    // Missing exhausted → readiness gate.
    state.phase = POSITION_IMPORT_PHASE.GATE;
    return [buildReadinessGateMessage()];
  };

  const resolveGate = (choice) => {
    if (choice === POSITION_IMPORT_ACTION.PROCEED) {
      state.phase = POSITION_IMPORT_PHASE.DONE;
    }
    // "Not yet" keeps the gate open — caller re-posts the same question.
    return [];
  };

  return {
    state,
    initialMessage,
    applyExtraction,
    resolveTitleMismatch,
    resolveOptIn,
    loadImprovements,
    currentImprovement,
    resolveImprovement,
    loadMissing,
    currentMissing,
    resolveMissing,
    resolveGate,
  };
}
